import React, { useState, useEffect, useRef } from 'react';
import {
  FileImage, Trash2, RefreshCw, Eye, CheckCircle2, AlertCircle,
  Clock, Loader, Upload, Search, Filter, Download, X,
  BarChart3, HardDrive, Users, TrendingUp, ChevronDown, Play
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  uploaded:   { label: 'Uploaded',   color: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500',   icon: Upload },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700',  dot: 'bg-amber-500',  icon: Loader },
  processed:  { label: 'Processed',  color: 'bg-teal-100 text-teal-700',    dot: 'bg-teal-500',   icon: CheckCircle2 },
  saved:      { label: 'Saved',      color: 'bg-green-100 text-green-700',  dot: 'bg-green-500',  icon: CheckCircle2 },
  failed:     { label: 'Failed',     color: 'bg-red-100 text-red-700',      dot: 'bg-red-500',    icon: AlertCircle },
  deleted:    { label: 'Deleted',    color: 'bg-gray-100 text-gray-500',    dot: 'bg-gray-400',   icon: Trash2 },
};

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

// ─── File Preview Modal ───────────────────────────────────────────────────────
function FilePreviewModal({ file, onClose }) {
  const { authFetch } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgError, setImgError] = useState('');
  const objRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setImgError('');
    setBlobUrl(null);
    // Use file_path (stored as userId/filename) or fall back to user_id + stored_name
    const viewPath = file.file_path
      ? `/api/files/view/${file.file_path}`
      : `/api/files/view/${file.user_id}/${file.stored_name}`;
    authFetch(viewPath)
      .then(r => {
        if (!r.ok) return r.text().then(t => { throw new Error(`${r.status}: ${t}`); });
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objRef.current = url;
        setBlobUrl(url);
      })
      .catch(err => { if (!cancelled) setImgError(err.message); });
    return () => {
      cancelled = true;
      if (objRef.current) { URL.revokeObjectURL(objRef.current); objRef.current = null; }
    };
  }, [file.id]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <div className="font-semibold text-gray-800 truncate max-w-md">{file.original_name}</div>
            <div className="text-xs text-gray-400 mt-0.5">{fmtSize(file.file_size)} · {fmtDate(file.uploaded_at)}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4 min-h-[300px]">
          {imgError ? (
            <div className="text-center text-gray-400 space-y-2 px-4">
              <AlertCircle className="w-10 h-10 mx-auto opacity-40" />
              <p className="text-sm font-medium">Could not load image</p>
              <p className="text-xs text-red-400 font-mono break-all">{imgError}</p>
            </div>
          ) : !blobUrl ? (
            <span className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          ) : (
            <img
              src={blobUrl}
              alt={file.original_name}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
              style={{ background: '#fff' }}
            />
          )}
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
          <span>Uploaded by: <strong>{file.user_name || 'Unknown'}</strong></span>
          <span>Records extracted: <strong>{file.records_extracted ?? '—'}</strong></span>
          <span>Records saved: <strong>{file.records_saved ?? '—'}</strong></span>
        </div>
      </div>
    </div>
  );
}

// ─── Reprocess Result Modal ───────────────────────────────────────────────────
function ReprocessResult({ result, onClose, onLoadIntoWorkflow }) {
  if (!result) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-teal-600" />
        </div>
        <h3 className="font-semibold text-gray-800 text-center mb-1">Reprocess Complete</h3>
        <p className="text-gray-500 text-sm text-center mb-5">
          Extracted <strong>{result.data?.rows?.length || 0} records</strong> from this file.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
            Close
          </button>
          <button onClick={() => onLoadIntoWorkflow(result)} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium">
            Load in Workflow
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────
function StatsBar({ stats }) {
  if (!stats) return null;
  const statusOrder = ['uploaded', 'processing', 'processed', 'saved', 'failed'];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {statusOrder.map(s => {
        const item = stats.byStatus?.find(x => x.status === s);
        const cfg = STATUS[s];
        const Icon = cfg.icon;
        return (
          <div key={s} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-800">{item?.count || 0}</div>
              <div className="text-xs text-gray-500">{cfg.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main FileManager Component ───────────────────────────────────────────────
export default function FileManager({ onLoadFileIntoWorkflow, users = [], adminMode = false }) {
  const { authFetch, isAdmin } = useAuth();
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [preview, setPreview] = useState(null);
  const [reprocessing, setReprocessing] = useState(null); // fileId being reprocessed
  const [reprocessResult, setReprocessResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('table'); // 'table' | 'board'
  const fileInputRef = useRef();

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterUser) params.set('user_id', filterUser);
      const res = await authFetch(`/api/files?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setFiles(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await authFetch('/api/files/stats');
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  useEffect(() => { fetchFiles(); if (isAdmin || adminMode) fetchStats(); }, [filterStatus, filterUser]);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    const formData = new FormData();
    selectedFiles.forEach(f => formData.append('files', f));
    try {
      const res = await authFetch('/api/files/upload', { method: 'POST',
        headers: {}, body: formData }); // no Content-Type, browser sets multipart
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      fetchFiles();
    } catch (err) { setError(err.message); }
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`/api/files/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setDeleteConfirm(null);
      fetchFiles();
    } catch (err) { setError(err.message); }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await authFetch('/api/files', { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setDeleteAllConfirm(false);
      fetchFiles();
      fetchStats();
    } catch (err) { setError(err.message); }
  };

  const handleReprocess = async (file) => {
    setReprocessing(file.id);
    setError('');
    try {
      const res = await authFetch(`/api/files/${file.id}/reprocess`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setReprocessResult(data);
      fetchFiles();
    } catch (err) { setError(err.message); }
    finally { setReprocessing(null); }
  };

  const filtered = files.filter(f =>
    (!search ||
      f.original_name.toLowerCase().includes(search.toLowerCase()) ||
      (f.user_name || '').toLowerCase().includes(search.toLowerCase()))
  );

  // Group by status for board view
  const boardGroups = ['uploaded', 'processing', 'processed', 'saved', 'failed'];

  return (
    <div className="space-y-5">
      {/* Stats */}
      {(isAdmin || adminMode) && <StatsBar stats={stats} />}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search files…"
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Statuses</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {(isAdmin || adminMode) && users.length > 0 && (
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <button onClick={() => { fetchFiles(); fetchStats(); }}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200">
            <RefreshCw className="w-4 h-4" />
          </button>
          {/* View toggle */}
          <div className="flex items-center gap-0.5 bg-gray-100 p-0.5 rounded-lg">
            <button onClick={() => setView('table')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === 'table' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
              Table
            </button>
            <button onClick={() => setView('board')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === 'board' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
              Board
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
          {(isAdmin || adminMode) && files.length > 0 && (
            <button onClick={() => setDeleteAllConfirm(true)}
              className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-4 py-2 rounded-lg text-sm font-medium">
              <Trash2 className="w-4 h-4" /> Delete All
            </button>
          )}
          <button onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Upload className="w-4 h-4" /> Upload Files
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── TABLE VIEW ──────────────────────────────────────────────────────── */}
      {view === 'table' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileImage className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No files found</p>
              <p className="text-sm mt-1">Upload files to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="text-left px-4 py-3">File</th>
                    {(isAdmin || adminMode) && <th className="text-left px-4 py-3 hidden md:table-cell">Uploaded By</th>}
                    <th className="text-left px-4 py-3 hidden sm:table-cell">Size</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Records</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Date</th>
                    <th className="px-4 py-3 w-32"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(f => {
                    const cfg = STATUS[f.status] || STATUS.uploaded;
                    const Icon = cfg.icon;
                    const isReprocessing = reprocessing === f.id;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center shrink-0">
                              <FileImage className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-800 text-sm truncate max-w-[180px]">{f.original_name}</div>
                              <div className="text-xs text-gray-400 sm:hidden">{fmtSize(f.file_size)}</div>
                            </div>
                          </div>
                        </td>
                        {(isAdmin || adminMode) && (
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="text-xs text-gray-700 font-medium">{f.user_name || '—'}</div>
                            <div className="text-xs text-gray-400">{f.user_email || ''}</div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{fmtSize(f.file_size)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${cfg.color}`}>
                            {isReprocessing
                              ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                              : <Icon className="w-3 h-3" />
                            }
                            {isReprocessing ? 'Processing…' : cfg.label}
                          </span>
                          {f.error_message && (
                            <div className="text-xs text-red-500 mt-1 max-w-[200px] truncate" title={f.error_message}>
                              {f.error_message}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="text-xs">
                            <span className="text-gray-600">{f.records_extracted ?? 0} extracted</span>
                            {f.records_saved > 0 && <span className="text-green-600 ml-2">{f.records_saved} saved</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-500">
                          <div>{fmtDate(f.uploaded_at)}</div>
                          {f.processed_at && <div className="text-gray-400">{fmtDate(f.processed_at)}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setPreview(f)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Preview">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            {(f.status === 'uploaded' || f.status === 'failed' || f.status === 'processed') && (
                              <button onClick={() => handleReprocess(f)} disabled={isReprocessing}
                                className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg disabled:opacity-40" title="Re-process with AI">
                                <Play className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {onLoadFileIntoWorkflow && f.status === 'processed' && (
                              <button onClick={() => onLoadFileIntoWorkflow(f)}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Load into workflow">
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => setDeleteConfirm(f)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                {filtered.length} of {files.length} files
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BOARD VIEW ──────────────────────────────────────────────────────── */}
      {view === 'board' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {boardGroups.map(status => {
            const cfg = STATUS[status];
            const Icon = cfg.icon;
            const group = filtered.filter(f => f.status === status);
            return (
              <div key={status} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className={`px-4 py-3 border-b border-gray-100 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                    <span className="font-semibold text-gray-700 text-sm">{cfg.label}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{group.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px] max-h-[500px] overflow-y-auto">
                  {group.length === 0 ? (
                    <div className="text-center py-8 text-gray-300 text-xs">Empty</div>
                  ) : group.map(f => (
                    <div key={f.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-blue-200 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-xs font-medium text-gray-800 truncate leading-tight">{f.original_name}</div>
                        <button onClick={() => setDeleteConfirm(f)} className="text-gray-300 hover:text-red-400 shrink-0">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      {(isAdmin || adminMode) && (
                        <div className="text-xs text-gray-400 mb-2">{f.user_name}</div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">{fmtSize(f.file_size)}</span>
                        <div className="flex gap-1">
                          <button onClick={() => setPreview(f)} className="p-1 text-gray-400 hover:text-blue-500 rounded">
                            <Eye className="w-3 h-3" />
                          </button>
                          {(status === 'uploaded' || status === 'failed') && (
                            <button onClick={() => handleReprocess(f)} disabled={reprocessing === f.id}
                              className="p-1 text-gray-400 hover:text-teal-500 rounded disabled:opacity-40">
                              <Play className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      {f.error_message && (
                        <div className="text-xs text-red-400 mt-1 truncate" title={f.error_message}>{f.error_message}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}

      {/* Reprocess result */}
      {reprocessResult && (
        <ReprocessResult
          result={reprocessResult}
          onClose={() => setReprocessResult(null)}
          onLoadIntoWorkflow={(r) => {
            if (onLoadFileIntoWorkflow) onLoadFileIntoWorkflow(r);
            setReprocessResult(null);
          }}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Delete File</h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              Delete <strong className="text-gray-700">{deleteConfirm.original_name}</strong>?<br />
              <span className="text-red-500">The physical file will also be removed from the server.</span>
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirm */}
      {deleteAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-800 text-center mb-1">Delete All Files?</h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              This will permanently delete all <strong>{files.length}</strong> files and their physical copies from the server. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteAllConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDeleteAll}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium">
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
